<script>
import { getPrograms, getProgramOptions, saveApplication } from '@/app/api';
import {mapActions, mapState} from 'vuex';
import {PMT, IRR, FV} from 'formulajs';
import {isNumber} from "@incodeapps/js-utilities";

export default {
	filters: {
		formatMoney(value) {
			return new Intl.NumberFormat('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2
			}).format(Number(value || 0));
		},
		formatPercent(value) {
			return Number(value || 0).toFixed(2);
		}
	},
	props: {
		application: {
			type: Object,
			default: null
		},
		getApplication: {
			type: Function,
			default: () => {}
		},
		goToNextStep: {
			type: Function,
			default: () => {}
		},
		nextStep: {
			type: Boolean,
			default: false
		},
		setNextDisabled: {
			type: Function,
			default: () => {}
		},
		showConfirmation: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		...mapState({
			authUser: state => state.auth.authUser,
		}),
	},
	data() {
		return {
			amortTerms: [],
			buyDown: {},
			deferral: {},
			deferralPeriods: [],
			equalPayment: {},
			frequencies: {},
			isActiveBtn: false,
			isInitializing: true, // Flag to prevent watchers during initialization
			leasing: {},
			paymentTerm: [
				{text: 'Select Financing Program first', value: 'default'}
			],
			programOptions: null, // Cache program options for validation
			programs: [],
			programTypes: [],
			rates: [],
			standard: {},
			submitting: false,
			terms: [],
			vModels: {
				actualTerm: null,
				amortizationTerm: null,
				amortTerm: null,
				chargeCodeOneAmount: null,
				chargeCodeOneBeforeMinMax: null,
				chargeCodeOneIsFinanced: null,
				chargeCodeOneMax: null,
				chargeCodeOneMin: null,
				chargeCodeOnePercent: null,
				chargeCodeOneWithMinMax: null,
				chargeCodeTwoAmount: null,
				chargeCodeTwoPercent: null,
				dealerAdminFee: null,
				dealerCost: null,
				dealerCostRate: null,
				deferralPeriod: null,
				downPayment: null,
				exactPeriodPayment: null,
				fee: null,
				financeTerm: null,
				financedAmount: 1000,
				frequency: null,
				frequencyName: null,
				intermediateAmortization: null,
				intermediateTerm: null,
				invoicedAmount: 1000,
				numberPaymentsInAmortization: null,
				numberPaymentsInTerm: null,
				paymentTerm: "default",
				paymentsPerYear: null,
				periodPayment: null,
				periodRate: null,
				program: null,
				programId: null,
				programType: null,
				rate: null,
				standardProgramRate: null,
				term: null
			}
		};
	},
	watch: {
		'vModels.invoicedAmount': {
			handler(val) {
				this.doCalculations();
			}
		},
		'vModels.programTypeBAK': {
			async handler(val) {
				this.saveLoading(true);
				this.programs = null;
				this.rates = null;
				this.amortTerms = null;
				this.vModels.program = null;
				this.vModels.rate = null;
				this.vModels.actualTerm = null;
				this.vModels.exactPeriodPayment = null;
				this.vModels.deferralPeriod = null;
				let valuesArray
				if (val === 'equalPayment') {
					this.programs = this.equalPayment;
					this.vModels.programType = 'equalPayment'
					valuesArray = Object.values(this.equalPayment);
				} else if (val === 'deferral') {
					this.programs = this.deferral;
					valuesArray = Object.values(this.deferral);
				} else if (val === 'standard') {
					this.programs = this.standard;
					valuesArray = Object.values(this.standard);
				} else {
					this.programs = this.buyDown;
					valuesArray = Object.values(this.buyDown);
				}
				let programId = valuesArray?.[0]?.ProgramID?.toString();
				this.vModels.program = valuesArray?.[0];
				this.vModels.program.ProgramID = programId;
				this.vModels.programId = programId;
				this.saveLoading(false);
			}
		},
		'vModels.program': {
			async handler(val, oldVal) {
				console.log('[vModels.program watcher] triggered - val:', val?.ProgramID, 'oldVal:', oldVal?.ProgramID, 'isInitializing:', this.isInitializing);
				// Skip during initialization to prevent double API calls
				if (this.isInitializing) {
					console.log('[vModels.program watcher] Skipping - component is still initializing');
					return;
				}
				if (val == null || !val) {
					return;
				}
				// Skip if the program hasn't actually changed
				if (oldVal && val.ProgramID === oldVal.ProgramID) {
					console.log('[vModels.program watcher] Program ID unchanged, skipping');
					return;
				}
				console.log('[vModels.program watcher] Resetting options and getting new ones...');
				this.rates = null;
				this.amortTerms = null;
				this.vModels.rate = null;
				this.terms = null;
				this.saveLoading(true);
				let payload = {
					programId: val.ProgramID
				};
				await this.getProgramOptions(payload);
				this.saveLoading(false);
			}
		},
		'vModels.term': {
			handler(val) {
				if (this.vModels.programType === 'equalPayment') {
					this.vModels.amortTerm = this.vModels.term
				}
				this.doCalculations()
			}
		},
		'vModels.deferralPeriod': {
			handler(val) {
				console.log('deferralPeriod changed');
				this.doCalculations()
			}
		},
		'vModels.amortTerm': {
			handler(val) {
				this.doCalculations()
			}
		},
		'vModels.rate': {
			handler(val) {
				this.doCalculations()
			}
		},
		'vModels.frequency': {
			handler(val) {
				this.doCalculations()
			}
		},
		"nextStep": "saveAndGo"
	},
	async mounted() {
		this.saveLoading(true);
		this.setNextDisabled(true);
		const id = this.$route.params.applicationId;
		if (id === 'new') {
			this.app = {};
		} else {
			this.app = this.application;
		}
		let hasExistingProgram = false;
		if (this.app?.application?.program) {
			Object.keys(this.app.application.program).forEach((element, i) => {
				this.vModels[element] = this.app.application.program[element]
			});
			hasExistingProgram = true;
		}
		await this.getPrograms(hasExistingProgram);
		let assignedType = false;
		let finalTypes = [];
		this.programTypes.forEach((type) => {//TODO: move this into the getPrograms function
			const programType = type.value;
			if (Object.keys(this[programType]).length > 0) {
				finalTypes.push(type);
				if (!hasExistingProgram && !assignedType) {
					this.vModels.programType = programType
					assignedType = true
				}
			}
		});
		this.programTypes = finalTypes
		this.vModels.coApps = [];
		if (!hasExistingProgram) {
			if (this.vModels.program) {
				await this.getProgramOptions({ programId: this.vModels.programId });
			}
		} else {
			await this.getProgramOptionsWithoutDefaults();
			let hasInvalidValue = false;
			let hasValuesToValidate = false;
			const programOptions = this.programOptions;
			const isDeferralProgram = this.vModels?.programType?.toLowerCase?.() === 'deferral';
			const isEqualPaymentProgram = this.vModels?.programType === 'equalPayment';
			if (!programOptions) {
				console.warn('[PaymentCalculator] No programOptions available for validation');
			} else {//TODO: make into a helper function with flags on how to validate
				if (this.vModels?.rate && programOptions.rates) {
					hasValuesToValidate = true;
					const validRate = Array.isArray(programOptions.rates)
						? programOptions.rates.includes(this.vModels.rate) || programOptions.rates.some(r => (typeof r === 'object' && r.name === this.vModels.rate))
						: programOptions.rates === this.vModels.rate || (programOptions.rates.name && programOptions.rates.name === this.vModels.rate);
					if (!validRate) {
						hasInvalidValue = true;
					}
				}
				if (this.vModels?.frequency && programOptions.frequencies) {
					hasValuesToValidate = true;
					const validFrequency = Object.keys(programOptions.frequencies).includes(String(this.vModels.frequency));
					if (!validFrequency) {
						console.warn(`Invalid Frequency: ${this.vModels.frequency}, Available Frequencies: ${JSON.stringify(programOptions.frequencies)}`);
						hasInvalidValue = true;
					}
				}
				if (this.vModels?.amortTerm && programOptions.amortTerms && !isEqualPaymentProgram) {
					hasValuesToValidate = true;
					const validAmortTerm = programOptions.amortTerms.includes(Number(this.vModels.amortTerm));
					if (!validAmortTerm) {
						console.warn(`Invalid Amortization Term: ${this.vModels.amortTerm}, Available Amortization Terms: ${JSON.stringify(programOptions.amortTerms)}`);
						hasInvalidValue = true;
					}
				}
				if (this.vModels?.term && programOptions.terms) {
					hasValuesToValidate = true;
					const validTerm = Array.isArray(programOptions.terms)
						? programOptions.terms.includes(this.vModels.term)
						: programOptions.terms === this.vModels.term;
					if (!validTerm) {
						console.warn(`Invalid Term: ${this.vModels.term}, Available Amortization Terms: ${JSON.stringify(programOptions.terms)}`);
						hasInvalidValue = true;
					}
				}
				if (this.vModels?.deferralPeriod && programOptions.deferralPeriods && isDeferralProgram) {
					hasValuesToValidate = true;
					const validDeferralPeriod = programOptions.deferralPeriods.includes(Number(this.vModels.deferralPeriod));
					if (!validDeferralPeriod) {
						console.warn(`Invalid Deferral Period: ${this.vModels.deferralPeriod}, Available Deferral Periods: ${JSON.stringify(programOptions.deferralPeriods)}`);
						hasInvalidValue = true;
					}
				}
				if (hasValuesToValidate) {
					if (hasInvalidValue) {
						await this.showErrorAlert('Program has changed. Some values are no longer available.');
					}
				}
			}
		}
		this.saveLoading(false);
		this.setNextDisabled(false);
		this.isInitializing = false;
	},
	methods: {
		...mapActions([
			'removeAlerts',
			'saveLoading',
			'showErrorAlert'
		]),
		filterValidFrequencies(frequencies) {
			const validFrequencyFormats = [
				"monthly",
				"semi-monthly",
				"bi-weekly",
				"weekly"
			];
			const filteredFrequencies = {};
			Object.keys(frequencies).forEach(key => {
				const frequency = frequencies[key];
				if (frequency && frequency.en) {
					const frequencyLower = frequency.en.toLowerCase();
					const isValid = validFrequencyFormats.some(validFormat =>
						frequencyLower === validFormat
					);
					if (isValid) {
						filteredFrequencies[key] = frequency;
					}
				}
			});
			return filteredFrequencies;
		},
		toggleActiveBtn() {},
		closeDropdown(event) {
			const dropdown = event.target.closest('.dropdown');
			if (dropdown) {
				const button = dropdown.querySelector('[data-bs-toggle=dropdown]');
				if (button && button.getAttribute('aria-expanded') === 'true') {
					button.click();
				}
			}
		},
		changeProgram(program, programType) {
			if (this.vModels.programId !== program.ProgramID?.toString()) {
				this.vModels.amortTerm = null;
				this.vModels.amortizationTerm = null;
				this.vModels.deferralPeriod = null;
				this.vModels.term = null;
				this.vModels.actualTerm = null;
				this.vModels.rate = null;
				this.vModels.frequency = null;
				this.vModels.frequencyName = null;
				this.vModels.dealerCost = null;
				this.vModels.programCharges = null;
				this.vModels.downPayment = null;
				this.vModels.chargeCodeOneAmount = null;
				this.vModels.chargeCodeOnePercent = null;
				this.vModels.chargeCodeOneIsFinanced = null;
				this.vModels.chargeCodeOneMin = null;
				this.vModels.chargeCodeTwoAmount = null;
				this.vModels.chargeCodeTwoPercent = null;
				this.vModels.chargeCodeOneBeforeMinMax = null;
				this.vModels.chargeCodeOneMax = null;
				this.vModels.chargeCodeOneWithMinMax = null;
				this.vModels.fee = null;
				this.vModels.dealerCostRate = null;
				this.vModels.dealerCost = null;
				this.vModels.dealerAdminFee = null;
				if (!programType && program.Code) {
					const programCodes = {//TODO: make a lookup
						'2': 'leasing',
						'32': 'standard',
						'36': 'buyDown',
						'37': 'equalPayment',
						'38': 'deferral'
					};
					programType = programCodes[program.Code] || programType;
				}
				this.vModels.program = program;
				this.vModels.programType = programType;
				this.vModels.programId = program.ProgramID?.toString();
			}
		},
		async saveAndGo() {
			if (this.nextStep) {
				this.saveLoading(true);
				let id = this.$route?.params?.applicationId ?? "new";
				if (id === 'new') {
					id = undefined;
				}
				const result = await saveApplication({
						id: id,
						program: this.vModels
				});
				if (id) {
					await this.getApplication();
				}
				this.saveLoading(false);
				this.goToNextStep({ applicationId: result?.id });
			}
		},
		doCalculations() {
			let paymentFrequencies = {//TODO: make this a lookup in case there is a plan to make more frequencies
				"monthly": 12,
				"semi-monthly": 24,
				"bi-weekly": 26,
				"weekly": 52,
			}
			let frequencyLowerCase = 0
			if (
				this.vModels.frequency
				// && this.frequencies.length > 0
				&& this.frequencies[this.vModels.frequency]
				&& this.frequencies[this.vModels.frequency].en
			) {
				frequencyLowerCase = (this.frequencies[this.vModels.frequency].en).toLowerCase()
				this.vModels.frequencyName = this.frequencies[this.vModels.frequency].en
			}
			this.vModels.paymentsPerYear = paymentFrequencies[frequencyLowerCase] || 12 // Default to monthly if no valid frequency
			this.vModels.exactPeriodPayment = null
			this.vModels.periodRate = (this.vModels.rate / 100) / this.vModels.paymentsPerYear;
			this.vModels.financeTerm = this.vModels.actualTerm;
			this.vModels.amortizationTerm = this.vModels.amortTerm - (this.vModels.deferralPeriod ?? 0);
			this.vModels.phaseOneTerm = this.vModels.actualTerm;
			this.vModels.downpayment = 0; // hardcoded for now
			this.calculateFeesAndDealerCosts();
			this.vModels.amount = Number(this.vModels.invoicedAmount) + Number(this.vModels.fee) - Number(this.vModels.downpayment);
			this.vModels.financedAmount = this.vModels.amount;
			this.vModels.isStepLoan = this.isStepLoan();
			if (this.vModels.isStepLoan) {
				this.doStepLoanCalculations();
			} else {
				this.doStandardCalculations();
			}
		},
		isStepLoan() {
			let result = false;
			if (this.vModels?.program?.Code) {
				const edgeId = Number(this.vModels.program.Code);
				result = [36,38].includes(edgeId) && this.vModels.phaseOneTerm < 60; //TODO: make configurable
			}
			return result;
		},
		doStandardCalculations() {
			this.vModels.numberPaymentsInTerm = (Number(this.vModels.financeTerm) / 12) * Number(this.vModels.paymentsPerYear)
			this.vModels.numberPaymentsInAmortization = (Number(this.vModels.amortizationTerm) / 12) * Number(this.vModels.paymentsPerYear)
			this.vModels.intermediateAmortization = Math.pow((1 + this.vModels.periodRate), this.vModels.numberPaymentsInAmortization)
			if (this.vModels.financeTerm) {
				this.vModels.intermediateTerm = Math.pow((1 + this.vModels.periodRate), this.vModels.numberPaymentsInTerm)
			}
			if (this.vModels.periodRate < 0.0000001) {
				this.vModels.exactPeriodPayment = (this.vModels.amount / this.vModels.numberPaymentsInAmortization)
			} else {
				this.vModels.exactPeriodPayment = (this.vModels.amount * this.vModels.periodRate * this.vModels.intermediateAmortization) / (this.vModels.intermediateAmortization - 1)
			}
			if (isNumber(this.vModels.exactPeriodPayment)) {
				this.vModels.periodPayment = Math.round(this.vModels.exactPeriodPayment * 100) / 100;
				this.vModels.totalExactPayments = (this.vModels.exactPeriodPayment * this.vModels.numberPaymentsInTerm)
			}
			if (this.vModels.periodRate < 0.00001) {
				this.vModels.residualAfterTerm = this.vModels.amount - (this.vModels.numberPaymentsInTerm * this.vModels.periodPayment);
			} else {
				this.vModels.residualAfterTerm =
					(this.vModels.amount * this.vModels.intermediateTerm) -
					(this.vModels.periodPayment * (this.vModels.intermediateTerm - 1) / this.vModels.periodRate);
			}
			this.vModels.finalPayment = this.vModels.financeTerm === this.vModels.amortizationTerm
				? this.vModels.periodPayment + this.vModels.residualAfterTerm
				: this.vModels.periodPayment;
			this.vModels.totalPayments = (this.vModels.numberPaymentsInTerm - 1) * this.vModels.periodPayment + this.vModels.finalPayment
			this.vModels.residualAfterTermOnDueDate = Number(this.vModels.residualAfterTerm) * (1 + this.vModels.periodRate);
			if (this.vModels.financeTerm === this.vModels.amortizationTerm) {
				this.vModels.residual = 0.00;
			} else {
				this.vModels.residual = this.vModels.residualAfterTermOnDueDate;
			}
			this.vModels.totalObligation = this.vModels.totalPayments + this.vModels.residual
			this.vModels.costBorrowing = Number(this.vModels.totalObligation) - Number(this.vModels.amount)
		},
		doStepLoanCalculations() {
			const amount = this.vModels.amount;
			const downPayment = 0;
			const fee = this.vModels.fee;
			const phaseOneTerm = this.vModels.phaseOneTerm;
			const amortizationTerm = Number(this.vModels.amortizationTerm);
			const annualRateOne = Number(this.vModels.rate) / 100; //0.0299; // @TODO: replace this variable
			const annualRateTwo = Number(this.vModels.standardProgramRate) / 100; // @TODO: replace this variable
			const phaseTwoTerm = 60 - phaseOneTerm;
			const paymentsPerYear = this.vModels.paymentsPerYear;
			const numberPaymentsInPhaseOne = (phaseOneTerm/ 12) * paymentsPerYear;
			const numberPaymentsInPhaseTwo = (phaseTwoTerm / 12) * paymentsPerYear;
			const numberPaymentsInAmortization = (amortizationTerm / 12) * paymentsPerYear;
			const periodRateOne = annualRateOne / paymentsPerYear;
			const periodRateTwo = annualRateTwo / paymentsPerYear;
			const exactPeriodPaymentPhaseOne = PMT(
				periodRateOne,
				numberPaymentsInAmortization,
				-amount
			);
			const periodPaymentPhaseOne = Math.round(exactPeriodPaymentPhaseOne * 100) / 100;
			const totalPaymentsPhaseOne = Math.round(numberPaymentsInPhaseOne * periodPaymentPhaseOne * 100) / 100;
			const residualAfterPhaseOne = Math.round(
				Math.abs(
					FV(
						periodRateOne,
						numberPaymentsInPhaseOne,
						-periodPaymentPhaseOne,
						amount
					)
				) * 100
			) / 100;
			const exactPeriodPaymentPhaseTwo = PMT(
				periodRateTwo,
				numberPaymentsInAmortization - numberPaymentsInPhaseOne,
				-residualAfterPhaseOne,
			);
			const periodPaymentPhaseTwo = Math.round(exactPeriodPaymentPhaseTwo * 100) / 100;
			const residualAfterPhaseTwo = Math.round(
				Math.abs(
					FV(
						periodRateTwo,
						numberPaymentsInPhaseTwo,
						-periodPaymentPhaseTwo,
						residualAfterPhaseOne
					)
				) * 100
			) / 100;
			const residualAfterPhaseTwoOnDueDate = Math.round(residualAfterPhaseTwo * (1 + periodRateTwo) * 100) / 100;
			const residual = amortizationTerm === 60 ? 0.00 : residualAfterPhaseTwoOnDueDate;
			const finalPaymentPhaseTwo = amortizationTerm === 60 ? periodPaymentPhaseTwo + residualAfterPhaseTwo : periodPaymentPhaseTwo;
			const totalPaymentsPhaseTwo = (numberPaymentsInPhaseTwo - 1) * periodPaymentPhaseTwo + finalPaymentPhaseTwo;
			const totalPayments = totalPaymentsPhaseOne + totalPaymentsPhaseTwo;
			const totalObligation = Math.round((totalPayments + residual) * 100) / 100;
			const costBorrowing = Math.round((totalObligation - amount) * 100) / 100;
			const aprGuess = (annualRateOne + annualRateTwo) / 2;
			const irrArray = this.constructIRRArray(
				amount,
				paymentsPerYear,
				phaseOneTerm,
				amortizationTerm, phaseTwoTerm,
				periodPaymentPhaseOne,
				periodPaymentPhaseTwo,
				residualAfterPhaseTwoOnDueDate,
				finalPaymentPhaseTwo
			);
			const periodicRate = IRR(irrArray);
			const apr = Math.round(periodicRate * paymentsPerYear * 10000) / 10000;
			Object.assign(this.vModels, {
				annualRateOne: annualRateOne,
				annualRateTwo: annualRateTwo,
				apr: apr,
				aprGuess: aprGuess,
				costBorrowing: costBorrowing,
				downpayment: downPayment, //TODO: investigate why this is a different casing then everywhere else?
				exactPeriodPaymentPhaseOne: exactPeriodPaymentPhaseOne,
				exactPeriodPaymentPhaseTwo: exactPeriodPaymentPhaseTwo,
				fee: fee,
				finalPaymentPhaseTwo: finalPaymentPhaseTwo,
				numberPaymentsInAmortization: numberPaymentsInAmortization,
				numberPaymentsInPhaseOne: numberPaymentsInPhaseOne,
				numberPaymentsInPhaseTwo: numberPaymentsInPhaseTwo,
				paymentsPerYear: paymentsPerYear,
				periodPaymentPhaseOne: periodPaymentPhaseOne,
				periodPaymentPhaseTwo: periodPaymentPhaseTwo,
				periodRateOne: periodRateOne,
				periodRateTwo: periodRateTwo,
				phaseTwoTerm: phaseTwoTerm,
				residual: residual,
				residualAfterPhaseOne: residualAfterPhaseOne,
				residualAfterPhaseTwo: residualAfterPhaseTwo,
				residualAfterPhaseTwoOnDueDate: residualAfterPhaseTwoOnDueDate,
				totalObligation: totalObligation,
				totalPayments: totalPayments,
				totalPaymentsPhaseOne: totalPaymentsPhaseOne,
				totalPaymentsPhaseTwo: totalPaymentsPhaseTwo,
			});
		},
		constructIRRArray(amount, paymentsPerYear, phaseOneTerm, amortizationTerm, phaseTwoTerm, periodPaymentPhaseOne, periodPaymentPhaseTwo, residualAfterPhaseTwoOnDueDate, finalPaymentPhaseTwo) {
			const toNum = (v) => Number(v ?? 0);
			const toInt = (v) => Math.round(toNum(v));
			const pushRepeated = (arr, val, n) => { for (let i = 0; i < n; i++) arr.push(val); };
			const deferralPeriod = toNum(this.vModels.deferralPeriod || 0);
			const nP1 = toInt((phaseOneTerm / 12) * paymentsPerYear);
			const nP2 = toInt((phaseTwoTerm / 12) * paymentsPerYear);
			const hasDeferral = deferralPeriod > 0;
			const flows = [];
			flows.push(-amount);
			if (!hasDeferral && amortizationTerm === 60) {
				pushRepeated(flows, periodPaymentPhaseOne, nP1);
				pushRepeated(flows, periodPaymentPhaseTwo, Math.max(0, nP2 - 1));
				flows.push(finalPaymentPhaseTwo);
			} else if (!hasDeferral && amortizationTerm > 60) {
				pushRepeated(flows, periodPaymentPhaseOne, nP1);
				pushRepeated(flows, periodPaymentPhaseTwo, nP2);
				flows.push(residualAfterPhaseTwoOnDueDate);
			} else if (hasDeferral && amortizationTerm === 60) {
				pushRepeated(flows, 0, deferralPeriod);
				pushRepeated(flows, periodPaymentPhaseOne, nP1);
				pushRepeated(flows, periodPaymentPhaseTwo, Math.max(0, nP2 - 1));
				flows.push(finalPaymentPhaseTwo);
			} else {
				pushRepeated(flows, 0, deferralPeriod);
				pushRepeated(flows, periodPaymentPhaseOne, nP1);
				pushRepeated(flows, periodPaymentPhaseTwo, nP2);
				flows.push(residualAfterPhaseTwoOnDueDate);
			}
			return flows;
		},
		calculateFeesAndDealerCosts() {
			const {
				chargeCodeOneAmount,
				chargeCodeOneIsFinanced,
				chargeCodeOneMin,
				chargeCodeOnePercent,
				chargeCodeTwoPercent,
				downPayment,
				invoicedAmount,
			} = this.vModels;
			const chargeCodeOneBeforeMinMax = this.roundToCents((invoicedAmount - downPayment) * (chargeCodeOnePercent / 100));
			const chargeCodeOneMax = chargeCodeOneAmount;
			const chargeCodeOneWithMinMax = this.roundToCents(
				Math.max(chargeCodeOneMin, Math.min(chargeCodeOneBeforeMinMax, chargeCodeOneMax))
			);
			const fee = chargeCodeOneIsFinanced ? chargeCodeOneWithMinMax : 0;
			const dealerCostRate = chargeCodeTwoPercent;
			const dealerCost = this.roundToCents(chargeCodeTwoPercent * (invoicedAmount - downPayment) / 100);
			const dealerAdminFee = !chargeCodeOneIsFinanced ? chargeCodeOneWithMinMax : 0;
			Object.assign(this.vModels, {
				chargeCodeOneBeforeMinMax,
				chargeCodeOneMax,
				chargeCodeOneWithMinMax,
				fee,
				dealerCostRate,
				dealerCost,
				dealerAdminFee,
			});
		},
		roundToCents(val) {
			return Math.round(Number(val) * 100) / 100;
		},
		async getPrograms(hasExistingProgram) {
			const result = await getPrograms().catch(async error => {
				let errorMsg = error.toString();
				if (error.response && error.response.data && error.response.data.errorMessage) {
					errorMsg = error.response.data.errorMessage;
				}
				if (errorMsg) {
					this.errorAlert = await this.showErrorAlert(errorMsg);
				}
			});
			if ((Object.keys(result?.programs ?? {})?.length ?? 0) > 0) {
				let standard = [];
				let equalPayment = [];
				let buyDown = [];
				let deferral = [];
				let firstProgram
				Object.values(result.programs).forEach(program => {//TODO: move to lookups
					switch (program.Code) {
						case '2':
							break;
						case '32':
							standard.push(program);
							if (!firstProgram) {
								firstProgram = program;
							}
							break;
						case '36':
							buyDown.push(program);
							break;
						case '37':
							equalPayment.push(program);
							break;
						case '38':
							deferral.push(program);
							break;
						default:
							break;
					}
				});
				if (!hasExistingProgram && firstProgram) {
					this.changeProgram(firstProgram);
				}
				this.buyDown = buyDown;
				this.equalPayment = equalPayment;
				this.standard = standard;
				this.deferral = deferral;
				this.programTypes = [//TODO: move to lookups
					{ text: 'Standard', value: 'standard', options: standard},
					{ text: 'Equal Payment', value: 'equalPayment', options: equalPayment },
					{ text: 'Buy Down', value: 'buyDown', options: buyDown },
					{ text: 'Deferral', value: 'deferral', options: deferral }
				];
				this.programs = null;
			} else {
				this.errorAlert = await this.showErrorAlert('No programs were found');
			}
		},
		async getProgramOptions(program) {
			if (program.programId) {
				this.submitting = true;
				const result = await getProgramOptions(program).catch(async error => {
					this.saveLoading(false);
					let errorMsg = error.toString();
					if (error.response && error.response.data && error.response.data.errorMessage) {
						errorMsg = error.response.data.errorMessage;
					}
					if (errorMsg) {
						this.errorAlert = await this.showErrorAlert(errorMsg);
					}
				});
				if (result) {
					const isDeferralProgram = this.vModels?.programType?.toLowerCase?.() === 'deferral';
					const isEqualPaymentProgram = this.vModels?.programType === 'equalPayment';
					const isStandardProgram = this.vModels?.programType === 'standard';
					if (this.vModels.program && isStandardProgram) {
						this.vModels.standardProgramRate = result.rates[result.rates.length - 1];
					}
					if (result?.rates) {
						this.rates = result.rates;
						this.vModels.rate = result.rates[this.rates.length - 1]
					}
					this.vModels.dealerCost = 0
					this.vModels.programCharges = 0
					if (Array.isArray(result?.programCharges)) {
						result.programCharges.forEach(charge => {
							if (charge) {
								const typeId = charge.ChargeTypeID?._text;
								const getNumber = val => (val && !isNaN(Number(val)) ? Number(val) : 0);
								if (typeId === '344') {
									this.vModels.chargeCodeOneAmount = getNumber(charge.Amount?._text);
									this.vModels.chargeCodeOnePercent = Math.abs(getNumber(charge.Percentage?._text));
									this.vModels.chargeCodeOneIsFinanced = charge.IsFinanced?._text === 'Y' ? true : false;
									this.vModels.chargeCodeOneMin = 0;// @TODO: get from Edge when we know where to get it
								}
								if (typeId === '346') {
									this.vModels.chargeCodeTwoAmount = getNumber(charge.Amount?._text);
									this.vModels.chargeCodeTwoPercent = Math.abs(getNumber(charge.Percentage?._text));
								}
							}
						});
					}
					if (result?.deferralPeriods && isDeferralProgram) {
						result.deferralPeriods.sort(function (a, b) {
							return (+a) - (+b);
						});
						this.deferralPeriods = result.deferralPeriods;
						this.vModels.deferralPeriod = this.deferralPeriods[this.deferralPeriods.length - 1]
					}
					if (result?.frequencies) {
						this.frequencies = this.filterValidFrequencies(result.frequencies)
						// Only set frequency if it's not already set (new program, not existing)
						if (!this.vModels.frequency && Object.keys(this.frequencies).length > 0) {
							// Try to find bi-weekly first
							let selectedFrequency = null;
							Object.keys(this.frequencies).forEach(key => {
								const frequency = this.frequencies[key];
								if (frequency && frequency.en && frequency.en.toLowerCase() === 'bi-weekly') {
									selectedFrequency = frequency.paymentPlanTypeId;
								}
							});
							if (!selectedFrequency) {
								selectedFrequency = this.frequencies[Object.keys(this.frequencies)[0]].paymentPlanTypeId;
							}
							this.vModels.frequency = selectedFrequency;
						}
					}
					this.paymentTerm = null;
					this.removeAlerts();
					if (!isEqualPaymentProgram && result?.amortTerms?.length) {
						this.amortTerms = result.amortTerms;
					}
					if (result?.terms) {
						this.terms = result.terms;
						this.vModels.term = this.terms[this.terms.length - 1]
						this.vModels.actualTerm = this.vModels.term - (this.vModels.deferralPeriod ?? 0)
					}
					if (isEqualPaymentProgram) {
						this.vModels.amortTerm = this.vModels.term;
					} else if (isDeferralProgram && this.amortTerms && this.amortTerms.length > 0) {
						this.vModels.amortTerm = this.amortTerms[this.amortTerms.length - 1];
					} else if (this.amortTerms && this.amortTerms.length > 0) {
						this.vModels.amortTerm = this.amortTerms[this.amortTerms.length - 1];
					}
					if (this.vModels.amortTerm) {
						if (isDeferralProgram) {
							this.vModels.amortizationTerm = this.vModels.amortTerm - (this.vModels.deferralPeriod ?? 0);
						} else {
							this.vModels.amortizationTerm = this.vModels.amortTerm;
						}
					}
					if (this.vModels.amortTerm && this.vModels.term) {
						this.doCalculations();
					}
				}
			}
		},
		async getProgramOptionsWithoutDefaults() {
			if (this.vModels?.programId) {
				const result = await getProgramOptions({programId: this.vModels.programId}).catch(async error => {
					let errorMsg = error.toString();
					if (error.response && error.response.data && error.response.data.errorMessage) {
						errorMsg = error.response.data.errorMessage;
					}
					if (errorMsg) {
						this.errorAlert = await this.showErrorAlert(errorMsg);
					}
				});
				if (result) {
					const isDeferralProgram = this.vModels?.programType?.toLowerCase?.() === 'deferral';
					const isEqualPaymentProgram = this.vModels?.programType === 'equalPayment';
					const isStandardProgram = this.vModels?.programType === 'standard';
					this.programOptions = result;
					if (result.rates) {
						this.rates = result.rates;
					}
					if (result?.frequencies) {
						this.frequencies = this.filterValidFrequencies(result.frequencies);
					}
					if (!isEqualPaymentProgram && result?.amortTerms?.length) {
						this.amortTerms = result.amortTerms;
					}
					if (result?.terms) {
						this.terms = result.terms;
					}
					if (result?.deferralPeriods && isDeferralProgram) {
						result.deferralPeriods.sort(function (a, b) {
							return (+a) - (+b);
						});
						this.deferralPeriods = result.deferralPeriods;
					}
					if (Array.isArray(result?.programCharges)) {
						result.programCharges.forEach(charge => {
							if (charge) {
								const typeId = charge.ChargeTypeID?._text;
								const getNumber = val => (val && !isNaN(Number(val)) ? Number(val) : 0);
								if (typeId === '344') {
									if (this.vModels.chargeCodeOneAmount === null || this.vModels.chargeCodeOneAmount === undefined) {
										this.vModels.chargeCodeOneAmount = getNumber(charge.Amount?._text);
									}
									if (this.vModels.chargeCodeOnePercent === null || this.vModels.chargeCodeOnePercent === undefined) {
										this.vModels.chargeCodeOnePercent = Math.abs(getNumber(charge.Percentage?._text));
									}
									if (this.vModels.chargeCodeOneIsFinanced === null || this.vModels.chargeCodeOneIsFinanced === undefined) {
										this.vModels.chargeCodeOneIsFinanced = charge.IsFinanced?._text === 'Y';
									}
									if (this.vModels.chargeCodeOneMin === null || this.vModels.chargeCodeOneMin === undefined) {
										this.vModels.chargeCodeOneMin = 0;
									}
								}
								if (typeId === '346') {
									if (this.vModels.chargeCodeTwoAmount === null || this.vModels.chargeCodeTwoAmount === undefined) {
										this.vModels.chargeCodeTwoAmount = getNumber(charge.Amount?._text);
									}
									if (this.vModels.chargeCodeTwoPercent === null || this.vModels.chargeCodeTwoPercent === undefined) {
										this.vModels.chargeCodeTwoPercent = Math.abs(getNumber(charge.Percentage?._text));
									}
								}
							}
						});
					}
					if (this.vModels.program && isStandardProgram) {
						this.vModels.standardProgramRate = result.rates[result.rates.length - 1];
					}
					this.doCalculations();
				}
			}
		},
			onSubmit() {
				this.submitting = true;
			}
	},
};
</script>

<template>
	<div>
		<div class="container-xl">
			<div class="row">
				<div class="col-24">
					<div class="template__page-title">
						<h1 v-if="!showConfirmation">Loan Payment Calculator</h1>
						<h1 v-if="showConfirmation">Installment Loan Estimate</h1>
					</div>
				</div>
			</div>
		</div>
		<form @submit.prevent="onSubmit" novalidate>
			<div class="container-xl template--vh">
				<div class="row">
					<div class="col-lg-16 order-2 order-lg-1" v-if="!showConfirmation">
						<div class="tffi-card tffi-card-main-card payment-calculator-main-card">
							<div class="payment-calculator-main-card__financed-amount">
								<label for="invoice-amount" class="theming primary-color fw-bold">Invoiced Amount</label>
								<input v-model="vModels.invoicedAmount" id="invoice-amount" name="invoicedAmount" type="number"
								       class="my-0" :placeholder="$t('financingProgram.invoicedAmount')">
							</div>
							<div class="payment-calculator-main-card__large-selector-area">
								<div class="payment-calculator-main-card__large-selector-area__label">
									<p>{{ $t("financingProgram.programType") }}</p>
								</div>
								<div class="payment-calculator-main-card__large-selector-area__buttons">
									<div :value="vModels.programType" name="programType" v-if="vModels.program">
										<div class="row h-100">
											<div v-for="(programType, index) in programTypes" :key="programType.value"
											     class="col-12 col-sm-12 col-lg-6 my-2 my-lg-3 dropdown position-relative-">
												<button :value="programType.value"
												        class="tffi-btn tffi-btn--solid theming primary-color dropdown-toggle" :class="{
													'payment-calculator-main-card__buttons--active theming primary-color': programType.value === vModels.programType
												}" type="button" data-bs-toggle="dropdown" data-bs-display="static" aria-expanded="false"
												        data-display="static" data-bs-boundary="window" data-bs-offset="100,200">
													{{ programType.text }}
												</button>
												<ul class="dropdown-menu dropdown-menu-end position-absolute- w-auto"
												    style="top:unset!important;right:unset!important;">
													<li v-for="option in programType.options"
													    :key="option.ProgramID"
													    class="dropdown-item px-3"
													    :class="{'dropdown-item-selected': Number(option.ProgramID) === Number(vModels.programId)}"
													    @click="changeProgram(option, programType.value); closeDropdown($event)">
														{{ option.Description }}
													</li>
												</ul>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div v-if="vModels.program" class="mb-4">
								<p>
									{{ vModels.program.Description }}
								</p>
							</div>
							<hr class="theming primary-color mt-0" />
							<div v-if="vModels.program && deferralPeriods && deferralPeriods.length > 0 && (vModels.programType == 'deferral' || vModels.programType == 'Deferral')">
								<div class="payment-calculator-main-card__small-selector-area mb-0">
									<div class="payment-calculator-main-card__small-selector-area__label mb-3">
										<p>Select Deferral options: </p>
									</div>
									<div class="payment-calculator-main-card__small-selector-area__buttons">
										<div :value="vModels.term" name="terms">
											<div class="row">
												<div v-for="(deferralPeriod, index) in deferralPeriods" class="col-12 col-sm-12 col-lg-6 mb-3">
													<button class="tffi-btn tffi-btn--solid"
													        :class="{'payment-calculator-main-card__buttons--active theming primary-color': deferralPeriod === vModels.deferralPeriod}"
													        :key="index" type="submit" :value="index"
													        :active="deferralPeriod === vModels.deferralPeriod"
													        @click="[(vModels.deferralPeriod = deferralPeriod), toggleActiveBtn()]">
														{{ deferralPeriod }} months
													</button>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div v-if="vModels.program && amortTerms && amortTerms.length > 0">
								<div class="payment-calculator-main-card__small-selector-area mb-0">
									<div class="payment-calculator-main-card__small-selector-area__label mb-3">
										<p>Amortization options: </p>
									</div>
									<div class="payment-calculator-main-card__small-selector-area__buttons">
										<div :value="vModels.term" name="terms">
											<div class="row">
												<div v-for="(amortTerm, index) in amortTerms" class="col-12 col-sm-12 col-lg-6 mb-3">
													<button class="tffi-btn tffi-btn--solid theming primary-color"
													        :class="{ 'payment-calculator-main-card__buttons--active theming primary-color': Number(amortTerm) === Number(vModels.amortTerm) }"
													        :key="index" type="submit" :value="index" :active="Number(amortTerm) === Number(vModels.amortTerm)"
													        @click="[(vModels.amortTerm = amortTerm), toggleActiveBtn()]">
														{{ amortTerm }} months
													</button>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div v-if="vModels.program && frequencies">
								<div class="payment-calculator-main-card__small-selector-area mb-0">
									<div class="payment-calculator-main-card__small-selector-area__label mb-3">
										<p>Payment Frequency: </p>
									</div>
									<div class="payment-calculator-main-card__small-selector-area__buttons">
										<div :value="vModels.term" name="terms">
											<div class="row">
												<div v-for="(frequency, index) in frequencies" class="col-12 col-sm-12 col-lg-6 mb-3">
													<button class="tffi-btn tffi-btn--solid theming primary-color"
													        :class="{ 'payment-calculator-main-card__buttons--active theming primary-color': frequency.paymentPlanTypeId === vModels.frequency }"
													        :key="index" type="submit" :value="index"
													        :active="frequency.paymentPlanTypeId === vModels.frequency"
													        @click="[(vModels.frequency = frequency.paymentPlanTypeId), toggleActiveBtn()]">
														{{ frequency.en }}
													</button>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>

						</div>
					</div>
					<div
						:class="(!showConfirmation === true) ? 'col-lg-8 col-t1 order-1 order-lg-2' : 'installment-loan-estimate-template col-sm-24 col-md-14 col-lg-10 mx-auto col-t2'">
						<div class="tffi-card tffi-card-summary-card">
							<div class="tffi-card-summary-card__top-area theming primary-color">
								<div class="tffi-card-summary-card__top-area__overlay-image"></div>
								<div class="tffi-card-summary-card__top-area__title-area">
									<h2>Installment loan estimate</h2>
								</div>
								<div class="tffi-card-summary-card__top-area__financed-amount">
									<p v-if="vModels.invoicedAmount">Invoiced Amount: <span
										class="tffi-card-summary-card__top-area__financed-amount__price">${{ vModels.invoicedAmount |
										formatMoney}}</span></p>
								</div>
								<div class="tffi-card-summary-card__top-area__financed-amount">
									<p>Search & Registration Fee: <span
										class="tffi-card-summary-card__top-area__financed-amount__price">${{ vModels.fee |
										formatMoney }}</span></p>
								</div>
								<div class="tffi-card-summary-card__top-area__financed-amount">
									<p v-if="vModels.isStepLoan && vModels.amount">Financed Amount: <span
										class="tffi-card-summary-card__top-area__financed-amount__price">${{ vModels.amount |
										formatMoney }}</span></p>
								</div>

								<div class="tffi-card-summary-card__top-area__financed-amount">
									<p v-if="!vModels.isStepLoan && vModels.financedAmount">Financed Amount: <span
										class="tffi-card-summary-card__top-area__financed-amount__price">${{ vModels.financedAmount |
										formatMoney }}</span></p>
								</div>
							</div>
							<div class="tffi-card-summary-card__bottom-area">
								<div v-if="vModels.programType" class="tffi-card-summary-card__bottom-area__label">
									<p class="mb-1">Program Type:</p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p class=" capitalize">{{ (vModels.programType ) }}</p>
								</div>
								<div v-if="vModels.deferralPeriod || true" class="tffi-card-summary-card__bottom-area__label">
									<p class="mb-1">Deferral Length:</p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p class=" capitalize">{{ (vModels.deferralPeriod ? vModels.deferralPeriod + ' Months' : 'NA') }}</p>
								</div>
								<div v-if="vModels.frequency && frequencies[vModels.frequency]" class="tffi-card-summary-card__bottom-area__label mb-0">
									<p class="">Payment Frequency:</p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p>{{ frequencies[vModels.frequency]['en'] }}</p>
								</div>
								<div v-if="vModels.phaseOneTerm && vModels.isStepLoan"
								     class="tffi-card-summary-card__bottom-area__label">
									<p :class="vModels.isStepLoan ? 'mb-1' : ''">Promotion Duration:</p><span
									class=" flex-grow-1 d-none d-lg-flex"></span>
									<p>{{ vModels.phaseOneTerm }} Months</p>
								</div>
								<div v-if="vModels.isStepLoan"
								     class="tffi-card-summary-card__bottom-area__label">
									<p :class="vModels.isStepLoan ? 'mb-1' : ''">Promotional Rate:</p><span
									class="flex-grow-1 d-none d-lg-flex"></span>
									<p>{{ ((vModels.annualRateOne || 0) * 100).toFixed(2) }}%</p>
								</div>
								<div v-if="vModels.periodPaymentPhaseOne && vModels.isStepLoan"
								     class="tffi-card-summary-card__bottom-area__label">
									<p class="theming primary-color fw-bold">Promotional Payment:</p><span class=" flex-grow-1 d-none
										d-lg-flex"></span>
									<p class="theming primary-color fw-bold">${{ vModels.periodPaymentPhaseOne | formatMoney }}</p>
								</div>
								<div v-if="vModels.phaseTwoTerm && vModels.isStepLoan"
								     class="tffi-card-summary-card__bottom-area__label">
									<p :class="vModels.isStepLoan ? 'mb-1' : ''">Remaining Duration:</p><span
									class="flex-grow-1 d-none d-lg-flex"></span>
									<p>{{ vModels.phaseTwoTerm }} Months</p>
								</div>
								<div v-if="vModels.rate != null && !vModels.isStepLoan"
								     class="tffi-card-summary-card__bottom-area__label">
									<p class="mb-1">Rate: </p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p>{{ vModels.rate }}%</p>
								</div>
								<div v-if="vModels.isStepLoan"
								     class="tffi-card-summary-card__bottom-area__label">
									<p class="mb-1">Rate:</p><span class="flex-grow-1 d-none d-lg-flex"></span>
									<p> {{ ((vModels.annualRateTwo || 0) * 100).toFixed(2) }}%</p>
								</div>
								<div v-if="vModels.periodPaymentPhaseTwo && vModels.isStepLoan"
								     class="tffi-card-summary-card__bottom-area__label">
									<p class="theming primary-color fw-bold">Payment:</p><span class=" flex-grow-1 d-none d-lg-flex">
									</span>
									<p class="theming primary-color fw-bold">${{ vModels.periodPaymentPhaseTwo | formatMoney }}
									</p>
								</div>
								<div v-if="vModels.amortTerm" class="tffi-card-summary-card__bottom-area__label">
									<p :class="!vModels.isStepLoan ?? 'mb-1'">Amortization Term:</p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p>{{ vModels.amortizationTerm }} Months</p>
								</div>
								<div v-if="vModels.aprGuess && vModels.isStepLoan" class="tffi-card-summary-card__bottom-area__label">
									<p>Effective APR:</p><span class="flex-grow-1 d-none d-lg-flex"></span>
									<p>{{ (vModels.apr * 100).toFixed(2) }}%</p>
								</div>
								<div v-if="vModels.leasingPeriod" class="tffi-card-summary-card__bottom-area__label">
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p>{{ $t('financingProgram.leasingPeriod') }}: {{ vModels.leasingPeriod || 'N/A' }} </p>
								</div>
								<div v-if="vModels.term && !vModels.isStepLoan" class="tffi-card-summary-card__bottom-area__label">
									<p>Finance Term:</p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p>{{ vModels.actualTerm }} Months</p>
								</div>
								<div v-if="vModels.periodPayment && !vModels.isStepLoan"
								     class="tffi-card-summary-card__bottom-area__label">
									<p class="theming primary-color fw-bold mb-0">Payment: </p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p class="theming primary-color fw-bold mb-0">${{ vModels.periodPayment | formatMoney }}</p>
								</div>
							</div>
						</div>
						<div v-if="!showConfirmation && authUser.usr_ShowDealerCosts" class="tffi-card tffi-card-summary-card">
							<div class="tffi-card-summary-card__bottom-area">
								<div class="tffi-card-summary-card__bottom-area__label">
									<p class="theming primary-color fw-bold mb-0">Dealer Cost Rate: </p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p class="theming primary-color fw-bold mb-0">{{ vModels.dealerCostRate | formatMoney }}%</p>
								</div>
								<div class="tffi-card-summary-card__bottom-area__label">
									<p class="theming primary-color fw-bold mb-0">Dealer Cost: </p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p class="theming primary-color fw-bold mb-0">${{ vModels.dealerCost | formatMoney }}</p>
								</div>
								<div class="tffi-card-summary-card__bottom-area__label">
									<p class="theming primary-color fw-bold mb-0">Dealer Admin Fee: </p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p class="theming primary-color fw-bold mb-0">${{ vModels.dealerAdminFee | formatMoney }}</p>
								</div>
							</div>
						</div>
						<pre class="d-none">{{ vModels }}</pre>
					</div>
				</div>
			</div>
		</form>
	</div>
</template>

<style lang="scss" scoped>

.btn-group {
	display: flex;
	flex-wrap: wrap;

	.btn {
		border-radius: $btn-border-radius !important;
		width: 30%;
		max-width: 31.5%;
		margin: 1rem 1%!important;

		&.lease-date {
			// margin: 1rem 1%;
			width: 10%;

			@media (max-width: 767px) {
				width: 100%;
				margin: 0.2rem 0%;
				padding: 10px!important;
			}
		}

		@media (max-width: 767px) {
			width: 100%;
			margin: 0.5rem 0%;

			&.program-btn {
				padding: 10px!important;
			}

			span.rate {
				font-size: 2rem!important;
			}

		}



		span {
			display: block;
		}

		&.btn-primary {
			&.active, &:active {
				background-color: $info !important;
			}
		}

	}

}

.dropdown-menu {
	min-width: max-content !important;
	// top: 100% !important;
	/* Ensures it appears right below the button */
	// left: 0 !important;
	transform: none !important;
	/* Override Bootstrap's transform */
	white-space: nowrap;
	overflow: visible !important;
	position: absolute !important;
	top: none!important;

	li {
		cursor: pointer;

		&.dropdown-item-selected {
			background-color: rgba(0, 123, 255, 0.1) !important;
			color: #007bff !important;
			font-weight: 600 !important;
		}
	}
}

.tffi-card-main-card {
	overflow: visible;
}
</style>
